import { Check, CloudUpload, Download, Warning } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Assignment } from '../../../../utils/types';

interface BulkAssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  onAssign: (assignments: Assignment[]) => Promise<void>;
  events: { id: string; name: string }[];
}

const BulkAssignmentDialog: React.FC<BulkAssignmentDialogProps> = ({ 
  open, 
  onClose, 
  onAssign,
  events 
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assignments, setAssignments] = useState<Assignment[]>([]);

  const steps = ['Upload CSV', 'Preview & Validate', 'Confirm'];

  useEffect(() => {
    // When dialog opens, verify our events list
    if (open) {
      console.log('Current events list:', events.map(e => ({
        id: e.id,
        name: e.name
      })));
    }
  }, [open, events]);

  const validateAssignments = async (rawAssignments: Assignment[]): Promise<Assignment[]> => {
    // Log available events for debugging
    console.log('Available events:', events.map(e => ({
      id: e.id,
      name: e.name
    })));

    return rawAssignments.map(assignment => {
      // Check each event ID against available events
      const validEvents = assignment.eventIds.filter(id => 
        events.some(e => e.id === id)
      );
      
      const invalidEvents = assignment.eventIds.filter(id => 
        !events.some(e => e.id === id)
      );

      return {
        ...assignment,
        eventIds: validEvents,
        eventNames: validEvents.map(id => {
          const event = events.find(e => e.id === id);
          return event ? event.name : 'Unknown Event';
        }),
        status: invalidEvents.length === 0 && validEvents.length > 0 ? 'valid' : 'invalid',
        error: invalidEvents.length > 0 
          ? `Invalid event IDs: ${invalidEvents.join(', ')}. Please check the available events list below.`
          : undefined
      };
    });
  };

  const handleEventIdMappingDownload = () => {
    // Create CSV content
    const header = 'Event Name,Event ID';
    const rows = events.map(event => `"${event.name}","${event.id}"`);
    const csvContent = [header, ...rows].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', 'event_ids.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setError(null);

    const reader = new FileReader();

    reader.onload = async (e) => {
        try {
            const text = e.target?.result;
            if (!text || typeof text !== 'string') {
                throw new Error('Failed to read file');
            }

            const lines: string[] = text.split('\n').map(line => line.trim()).filter(Boolean);
            if (lines.length < 2) {
                throw new Error('File is empty');
            }

            const parsedAssignments = lines
                .map(line => line.split(',').map(col => col.trim()))
                .filter(row => {
                    if (row.length < 2) {
                        console.warn('Skipping invalid row:', row);
                        return false;
                    }
                    const email = row[0];
                    const eventIds: string[] = row.slice(1);
                    if (!email || eventIds.length === 0) {
                        console.warn('Skipping row with missing data:', row);
                        return false;
                    }
                    // Validate email format
                    if(!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                        console.warn('Skipping row with invalid email:', email);
                        return false;
                    }
                    // check that the id is 20 alphanumeric characters
                    if (eventIds.some(id => !/^[a-zA-Z0-9]{20}$/.test(id))) {
                        console.warn('Skipping row with invalid event IDs:', eventIds);
                        return false;
                    }
                    return true;
                })
                .map(row => ({
                    email: row[0].toLowerCase(),
                    eventIds: row.slice(1),
                    eventNames: undefined, // Will be set after validation
                    status: 'valid',
                } as Assignment));

            if (parsedAssignments.length === 0) {
                throw new Error('No valid assignments found in the file');
            }

            // Log the parsed assignments for debugging
            console.log('Parsed assignments from CSV:', parsedAssignments);

            const validatedAssignments = await validateAssignments(parsedAssignments);

            // Log validation results
            console.log('Validation results:', validatedAssignments.map(a => ({
                email: a.email,
                status: a.status,
                validEventIds: a.eventIds,
                error: a.error,
            })));

            const validCount = validatedAssignments.filter(a => a.status === 'valid').length;

            if (validCount === 0) {
                throw new Error(
                    'No valid assignments found. Please check that your event IDs exactly match the available events. ' +
                    'Available event IDs: ' + events.map(e => e.id).join(', ')
                );
            }

            setAssignments(validatedAssignments);
            setActiveStep(1);
        } catch (err: any) {
            console.error('Error processing file:', err);
            setError(err.message || 'Failed to process file');
        } finally {
            setLoading(false);
        }
    };

    reader.onerror = () => {
        console.error('FileReader error:', reader.error);
        setError('Failed to read file');
        setLoading(false);
    };

    reader.readAsText(file);
};

  const handleConfirm = async () => {
    try {
      setLoading(true);
      setError(null);

      // Separate valid and invalid assignments
      const validAssignments = assignments.filter(a => a.status === 'valid');
      const invalidAssignments = assignments.filter(a => a.status === 'invalid');

      try {
        // Only assign the valid ones
        await onAssign(validAssignments);
        
        // If there were invalid assignments, show them as a warning instead of blocking
        if (invalidAssignments.length > 0) {
          const errorEmails = invalidAssignments.map(a => a.email);
          setError(`Successfully assigned ${validAssignments.length} users. Failed to assign ${invalidAssignments.length} users: ${errorEmails.join(', ')}`);
          // You might also want to pass this information back to the parent component
          // if you need to handle it elsewhere
        }
        
        setActiveStep(2);
      } catch (err: any) {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const renderUploadStep = () => (
    <Box sx={{ p: 2 }}>
      {events.length === 0 ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          No events available. Please close this dialog and try again.
        </Alert>
      ) : (
        <>
          <Typography variant="body1" gutterBottom>
            Upload a CSV file with the following format:
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', my: 2, p: 2, bgcolor: 'grey.100' }}>
            user@example.com,event_id_1,event_id_2
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Available Events
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Please verify that these events are up-to-date. If not, try closing and reopening this dialog.
            </Alert>
            <Paper sx={{ maxHeight: 300, overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Event Name</TableCell>
                    <TableCell>Event ID</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {events.map((event) => (
                    <TableRow key={event.id}>
                      <TableCell>{event.name}</TableCell>
                      <TableCell>
                        <Box sx={{ 
                          fontFamily: 'monospace',
                          bgcolor: 'grey.100',
                          p: 1,
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}>
                          {event.id}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Box>
          <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '15px',
          }}>
            <Button startIcon={<Download />} variant="outlined" onClick={handleEventIdMappingDownload}>
              Download 
            </Button>
            <Button
              variant="contained"
              component="label"
              startIcon={<CloudUpload />}
              disabled={loading}
            >
              Upload CSV
              <input
                type="file"
                hidden
                accept=".csv"
                onChange={handleFileUpload}
              />
            </Button>
          </Box>
        </>
      )}
    </Box>
  );

  const renderPreviewStep = () => (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Available Events
      </Typography>
      <Paper sx={{ mb: 3, p: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Event Name</TableCell>
              <TableCell>Event ID</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map((event) => (
              <TableRow key={event.id}>
                <TableCell>{event.name}</TableCell>
                <TableCell>
                  <Typography sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', p: 0.5 }}>
                    {event.id}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="h6" gutterBottom>
        CSV Preview
      </Typography>
      <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Event Names</TableCell>
              <TableCell>Event IDs</TableCell>
              <TableCell>Issues</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assignments.map((assignment, index) => (
              <TableRow key={index}>
                <TableCell>
                  {assignment.status === 'valid' ? (
                    <Check color="success" />
                  ) : (
                    <Warning color="error" />
                  )}
                </TableCell>
                <TableCell>{assignment.email}</TableCell>
                <TableCell>{assignment.eventNames?.join(', ')}</TableCell>
                <TableCell>
                  <Typography sx={{ fontFamily: 'monospace' }}>
                    {assignment.eventIds.join(', ')}
                  </Typography>
                </TableCell>
                <TableCell>{assignment.error}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );

  const renderSuccessStep = () => {
    const validCount = assignments.filter(a => a.status === 'valid').length;
    const invalidCount = assignments.filter(a => a.status === 'invalid').length;
    
    return (
      <Box sx={{ p: '20px', textAlign: 'center'}}>
        <Check color="success" sx={{ fontSize: 60, mb: '20px' }} />
        <Typography variant="h5">
          Assignments completed!
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Successfully assigned {validCount} users to events.
          {invalidCount > 0 && (
            <Typography variant="body2" color="warning.main">
              {invalidCount} assignments failed due to validation errors.
            </Typography>
          )}
        </Typography>
        <Typography variant="subtitle2" sx={{
          fontStyle: 'italic',
        }}>
          Please note, assignments are not saved until the schedule is published.
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk Assign Users to Events</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && (
          <>
            {activeStep === 0 && renderUploadStep()}
            {activeStep === 1 && renderPreviewStep()}
            {activeStep === 2 && renderSuccessStep()}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {activeStep === 1 && (
          <>
            <Button onClick={() => setActiveStep(0)}>Back</Button>
            <Button 
              variant="contained" 
              onClick={handleConfirm}
              disabled={!assignments.some(a => a.status === 'valid')}
            >
              Validate and Confirm
            </Button>
          </>
        )}
        {(activeStep === 0 || activeStep === 2) && (
          <Button onClick={onClose}>
            {activeStep === 2 ? 'Close' : 'Cancel'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkAssignmentDialog;